// src/components/auth/LoginSuccess.jsx
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jwtDecode } from 'jwt-decode'; // Changed import

const LoginSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Send the user to the error page with a visible reason instead of
    // silently bouncing back to login (which is impossible to diagnose).
    const fail = (reason, detail) => {
      console.error(`[LoginSuccess] ${reason}`, detail ?? '');
      navigate(`/login-error?message=${encodeURIComponent(reason)}`, { replace: true });
    };

    // Token is delivered via URL fragment (#token=...) to keep it out of
    // server logs and browser history. Fall back to query param for
    // backwards compatibility during deployment.
    const hash = window.location.hash;
    const token = hash.startsWith('#token=')
      ? hash.slice(7)
      : searchParams.get('token');

    if (!token) {
      return fail('No authentication token was received. Please try signing in again.');
    }

    let decodedToken;
    try {
      decodedToken = jwtDecode(token);
    } catch (err) {
      return fail('The authentication token was malformed. Please try signing in again.', err);
    }

    // Reject an already-expired token *before* storing it. Otherwise login()
    // saves it and AuthContext's expiry check immediately logs us back out,
    // which looks identical to "login silently failed". The most common cause
    // of a brand-new token reading as expired is a device clock that is set
    // ahead of real time.
    const expMs = decodedToken.exp ? decodedToken.exp * 1000 : 0;
    if (!expMs || expMs <= Date.now()) {
      return fail(
        'Your session expired immediately. This usually means your device clock is wrong — ' +
          'please set the date and time to update automatically, then sign in again.',
        `token exp=${expMs ? new Date(expMs).toISOString() : 'missing'}, ` +
          `device now=${new Date().toISOString()}`
      );
    }

    try {
      login(token, decodedToken);
    } catch (err) {
      return fail('Something went wrong while starting your session. Please try again.', err);
    }

    // Storage can silently no-op in private/incognito windows or when the
    // browser is configured to block site data. Verify the write actually
    // stuck before we trust it and scrub the URL.
    if (localStorage.getItem('wmf_token') !== token) {
      return fail(
        'Your browser blocked saving the login session. Check that cookies and site data ' +
          'are allowed for this site (and that you are not in a private window), then try again.'
      );
    }

    // Only now that the session is safely stored do we remove the token from
    // the URL, so a re-read of the hash can never lose it mid-flight.
    window.history.replaceState({}, '', window.location.pathname);
    navigate('/dashboard', { replace: true });
  }, [searchParams, login, navigate]);

  // Show loading state while redirecting
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md">
        <div className="flex items-center space-x-3">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Logging you in...</p>
        </div>
      </div>
    </div>
  );
};

export default LoginSuccess;
